import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { fade, makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import ReceiptIcon from "@material-ui/icons/Receipt";
import TuneIcon from "@material-ui/icons/Tune";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";

import ConfirmationModal from "../ConfirmationModal";
import RowActionsMenu from "../RowActionsMenu";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    minWidth: 0,
  },
  contentStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
  },
  hero: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  heroTitle: {
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  filtersPaper: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  filtersTitle: {
    fontWeight: 600,
  },
  filtersActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "stretch",
      "& > *": {
        flex: "1 1 100%",
      },
    },
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
    [theme.breakpoints.down("lg")]: {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  summaryCard: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 16,
    minHeight: 92,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${fade(theme.palette.divider, 0.8)}`,
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  summaryLabel: {
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    opacity: 0.7,
  },
  summaryValue: {
    fontSize: "1.5rem",
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(0.75),
  },
  helperText: {
    opacity: 0.72,
    marginTop: theme.spacing(0.5),
  },
  chip: {
    fontWeight: 600,
  },
  tablePaper: {
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  tableContainer: {
    width: "100%",
    minWidth: 0,
    maxHeight: "68vh",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  tableHeaderTitle: {
    fontWeight: 600,
  },
  rowPrivate: {
    backgroundColor: fade(theme.palette.warning.light, 0.1),
  },
  rowTrial: {
    backgroundColor: fade(theme.palette.info.light, 0.1),
  },
  emptyState: {
    padding: theme.spacing(5, 2),
    textAlign: "center",
  },
  nowrap: {
    whiteSpace: "nowrap",
  },
  actionsCell: {
    width: 1,
    whiteSpace: "nowrap",
  },
  dialogPaper: {
    borderRadius: 18,
  },
  dialogContent: {
    paddingTop: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  featureCard: {
    padding: theme.spacing(1.5),
    borderRadius: 12,
    border: `1px solid ${fade(theme.palette.divider, 0.9)}`,
  },
}));

const RECURRENCE_OPTIONS = [
  { value: "", label: "Sem recorrencia" },
  { value: "MENSAL", label: i18n.t("compaies.table.monthly") },
  { value: "BIMESTRAL", label: i18n.t("compaies.table.bimonthly") },
  { value: "TRIMESTRAL", label: i18n.t("compaies.table.quarterly") },
  { value: "SEMESTRAL", label: i18n.t("compaies.table.semester") },
  { value: "ANUAL", label: i18n.t("compaies.table.yearly") },
];

const FEATURE_FIELDS = [
  { key: "useWhatsapp", label: "WhatsApp" },
  { key: "useFacebook", label: "Facebook" },
  { key: "useInstagram", label: "Instagram" },
  { key: "useCampaigns", label: i18n.t("plans.form.campaigns") },
  { key: "useSchedules", label: i18n.t("plans.form.schedules") },
  { key: "useInternalChat", label: i18n.t("compaies.table.useInternalChat") },
  { key: "useExternalApi", label: i18n.t("compaies.table.useExternalApi") },
  { key: "useKanban", label: "Kanban" },
  { key: "useCrm", label: "CRM comercial" },
  { key: "useOpenAi", label: "Talk.Ai" },
  { key: "useIntegrations", label: "Integracoes" },
];

const SUMMARY_CARDS = [
  { key: "total", label: "Total de planos", accent: "#475569" },
  { key: "public", label: "Publicos", accent: "#2563eb" },
  { key: "private", label: "Privados", accent: "#7c3aed" },
  { key: "trial", label: "Com trial", accent: "#0f766e" },
  { key: "openAi", label: "Com Talk.Ai", accent: "#ea580c" },
  { key: "integrations", label: "Com integracoes", accent: "#16a34a" },
];

const INITIAL_FORM = {
  id: undefined,
  name: "",
  users: 0,
  connections: 0,
  queues: 0,
  amount: "0,00",
  useWhatsapp: true,
  useFacebook: true,
  useInstagram: true,
  useCampaigns: true,
  useSchedules: true,
  useInternalChat: true,
  useExternalApi: true,
  useKanban: true,
  useCrm: false,
  useOpenAi: true,
  useIntegrations: true,
  isPublic: true,
  trial: false,
  trialDays: 0,
  recurrence: "",
};

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  if (Number.isNaN(numeric)) {
    return "R$ 0,00";
  }

  return `R$ ${numeric.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const normalizeAmount = (value) => {
  if (typeof value === "number") {
    return value.toFixed(2);
  }

  const sanitized = String(value || "")
    .trim()
    .replace(/\./g, "")
    .replace(",", ".");

  const numeric = Number(sanitized);
  return Number.isNaN(numeric) ? "0.00" : numeric.toFixed(2);
};

const amountToInput = (value) => {
  const numeric = Number(value || 0);
  if (Number.isNaN(numeric)) {
    return "0,00";
  }

  return numeric.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const countEnabledFeatures = (plan) =>
  FEATURE_FIELDS.reduce(
    (total, feature) => total + (plan?.[feature.key] ? 1 : 0),
    0
  );

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  try {
    return format(parseISO(value), "dd/MM/yy HH:mm");
  } catch (error) {
    return "-";
  }
};

const PlansManager = () => {
  const classes = useStyles();
  const { list, save, update, remove } = usePlans();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [records, setRecords] = useState([]);
  const [filters, setFilters] = useState({
    searchParam: "",
    visibility: "",
    onlyTrial: false,
  });
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const plans = await list();
      setRecords(Array.isArray(plans) ? plans : []);
    } catch (error) {
      toast.error("Nao foi possivel carregar a lista de planos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = useMemo(
    () =>
      records.reduce(
        (acc, plan) => {
          acc.total += 1;
          acc.public += plan.isPublic ? 1 : 0;
          acc.private += plan.isPublic ? 0 : 1;
          acc.trial += plan.trial ? 1 : 0;
          acc.openAi += plan.useOpenAi ? 1 : 0;
          acc.integrations += plan.useIntegrations ? 1 : 0;
          return acc;
        },
        {
          total: 0,
          public: 0,
          private: 0,
          trial: 0,
          openAi: 0,
          integrations: 0,
        }
      ),
    [records]
  );

  const filteredRecords = useMemo(
    () =>
      records.filter((plan) => {
        const matchesSearch = [plan.name, plan.recurrence]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(filters.searchParam.trim().toLowerCase());

        const matchesVisibility =
          !filters.visibility ||
          (filters.visibility === "public" && plan.isPublic) ||
          (filters.visibility === "private" && !plan.isPublic);

        const matchesTrial = !filters.onlyTrial || Boolean(plan.trial);

        return matchesSearch && matchesVisibility && matchesTrial;
      }),
    [filters, records]
  );

  const hasActiveFilters = Boolean(
    filters.searchParam.trim() || filters.visibility || filters.onlyTrial
  );

  const resetForm = () => {
    setFormData(INITIAL_FORM);
  };

  const handleOpenCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const handleOpenEditModal = (plan) => {
    setFormData({
      id: plan.id,
      name: plan.name || "",
      users: plan.users ?? 0,
      connections: plan.connections ?? 0,
      queues: plan.queues ?? 0,
      amount: amountToInput(plan.amount),
      useWhatsapp: plan.useWhatsapp !== false,
      useFacebook: plan.useFacebook !== false,
      useInstagram: plan.useInstagram !== false,
      useCampaigns: plan.useCampaigns !== false,
      useSchedules: plan.useSchedules !== false,
      useInternalChat: plan.useInternalChat !== false,
      useExternalApi: plan.useExternalApi !== false,
      useKanban: plan.useKanban !== false,
      useCrm: plan.useCrm === true,
      useOpenAi: plan.useOpenAi !== false,
      useIntegrations: plan.useIntegrations !== false,
      isPublic: plan.isPublic !== false,
      trial: Boolean(plan.trial),
      trialDays: plan.trialDays ?? 0,
      recurrence: plan.recurrence || "",
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    if (saving) {
      return;
    }

    setModalOpen(false);
    resetForm();
  };

  const handleFilterChange = (field) => (event) => {
    const value =
      field === "onlyTrial" ? event.target.checked : event.target.value;

    setFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleFormFieldChange = (field) => (event) => {
    const value = event.target.value;

    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleBooleanFieldChange = (field) => (event) => {
    setFormData((current) => ({
      ...current,
      [field]: event.target.checked,
    }));
  };

  const clearFilters = () => {
    setFilters({
      searchParam: "",
      visibility: "",
      onlyTrial: false,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        users: Number(formData.users) || 0,
        connections: Number(formData.connections) || 0,
        queues: Number(formData.queues) || 0,
        trialDays: Number(formData.trialDays) || 0,
        amount: normalizeAmount(formData.amount),
      };

      if (payload.id) {
        await update(payload);
        toast.success("Plano atualizado com sucesso.");
      } else {
        await save(payload);
        toast.success("Plano criado com sucesso.");
      }

      await loadPlans();
      handleCloseModal();
    } catch (error) {
      toast.error(
        "Nao foi possivel salvar o plano. Verifique nome, valores e se ja existe um plano com esse nome."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!recordToDelete) {
      return;
    }

    setSaving(true);
    try {
      await remove(recordToDelete.id);
      toast.success("Plano excluido com sucesso.");
      await loadPlans();

      if (formData.id === recordToDelete.id) {
        handleCloseModal();
      }
    } catch (error) {
      toast.error("Nao foi possivel excluir o plano.");
    } finally {
      setSaving(false);
      setRecordToDelete(null);
    }
  };

  const renderVisibilityChip = (plan) => (
    <Chip
      size="small"
      label={plan.isPublic ? "Publico" : "Privado"}
      className={classes.chip}
      style={
        plan.isPublic
          ? { backgroundColor: "#dbeafe", color: "#1d4ed8" }
          : { backgroundColor: "#ede9fe", color: "#6d28d9" }
      }
    />
  );

  const renderTrialChip = (plan) => (
    <Chip
      size="small"
      label={plan.trial ? `${plan.trialDays || 0} dias` : "Sem trial"}
      className={classes.chip}
      style={
        plan.trial
          ? { backgroundColor: "#ccfbf1", color: "#115e59" }
          : { backgroundColor: "#e5e7eb", color: "#374151" }
      }
    />
  );

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title="Excluir plano"
        open={Boolean(recordToDelete)}
        onClose={() => setRecordToDelete(null)}
        onConfirm={handleDelete}
      >
        Deseja realmente excluir o plano
        {recordToDelete ? ` "${recordToDelete.name}"` : ""}?
      </ConfirmationModal>

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
        classes={{ paper: classes.dialogPaper }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {formData.id ? "Editar plano" : "Novo plano"}
          </DialogTitle>
          <DialogContent dividers className={classes.dialogContent}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography className={classes.sectionTitle}>
                  Dados principais
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  label={i18n.t("plans.form.name")}
                  value={formData.name}
                  onChange={handleFormFieldChange("name")}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label={i18n.t("plans.form.users")}
                  type="number"
                  value={formData.users}
                  onChange={handleFormFieldChange("users")}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label={i18n.t("plans.form.connections")}
                  type="number"
                  value={formData.connections}
                  onChange={handleFormFieldChange("connections")}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label={i18n.t("compaies.table.numberQueues")}
                  type="number"
                  value={formData.queues}
                  onChange={handleFormFieldChange("queues")}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label={i18n.t("compaies.table.value")}
                  value={formData.amount}
                  onChange={handleFormFieldChange("amount")}
                  helperText="Use virgula ou ponto para decimais"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  size="small"
                  label={i18n.t("plans.form.public")}
                  value={String(formData.isPublic)}
                  onChange={(event) =>
                    setFormData((current) => ({
                      ...current,
                      isPublic: event.target.value === "true",
                    }))
                  }
                >
                  <MenuItem value="true">{i18n.t("plans.form.yes")}</MenuItem>
                  <MenuItem value="false">{i18n.t("plans.form.no")}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  size="small"
                  label={i18n.t("compaies.table.recurrence")}
                  value={formData.recurrence}
                  onChange={handleFormFieldChange("recurrence")}
                >
                  {RECURRENCE_OPTIONS.map((option) => (
                    <MenuItem key={option.value || "empty"} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Dias de trial"
                  type="number"
                  value={formData.trialDays}
                  onChange={handleFormFieldChange("trialDays")}
                  disabled={!formData.trial}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" height="100%">
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={formData.trial}
                        onChange={handleBooleanFieldChange("trial")}
                      />
                    }
                    label="Plano com trial"
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box mt={1}>
                  <Typography className={classes.sectionTitle}>
                    Recursos habilitados
                  </Typography>
                </Box>
              </Grid>

              {FEATURE_FIELDS.map((feature) => (
                <Grid item xs={12} sm={6} md={4} key={feature.key}>
                  <Paper className={classes.featureCard} elevation={0}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gridGap={8}>
                        <ReceiptIcon fontSize="small" color="action" />
                        <Typography variant="body2">{feature.label}</Typography>
                      </Box>
                      <Switch
                        color="primary"
                        inputProps={{ "aria-label": feature.label }}
                        checked={Boolean(formData[feature.key])}
                        onChange={handleBooleanFieldChange(feature.key)}
                      />
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            {formData.id ? (
              <Button
                color="secondary"
                startIcon={<DeleteOutlineIcon />}
                onClick={() =>
                  setRecordToDelete({
                    id: formData.id,
                    name: formData.name,
                  })
                }
                disabled={saving}
              >
                Excluir
              </Button>
            ) : (
              <Box flex={1} />
            )}
            <Button onClick={handleCloseModal} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <div className={classes.contentStack}>
        <Box className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.heroTitle}>
              Gestao de planos
            </Typography>
          </Box>

          <Box className={classes.heroActions}>
            <Button
              variant="outlined"
              startIcon={<AutorenewIcon />}
              onClick={loadPlans}
              disabled={loading}
            >
              Atualizar
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateModal}
            >
              Novo plano
            </Button>
          </Box>
        </Box>

        <div className={classes.summaryGrid}>
          {SUMMARY_CARDS.map((card) => (
            <Paper className={classes.summaryCard} elevation={0} key={card.key}>
              <Typography className={classes.summaryLabel}>{card.label}</Typography>
              <Typography className={classes.summaryValue}>
                {summary[card.key] || 0}
              </Typography>
            </Paper>
          ))}
        </div>

        <Paper className={classes.filtersPaper} elevation={0}>
          <Box className={classes.filtersHeader}>
            <Typography variant="body1" className={classes.filtersTitle}>
              Filtros
            </Typography>
            <Box className={classes.filtersActions}>
              {hasActiveFilters ? <Chip size="small" label="Filtros ativos" /> : null}
              <Button onClick={clearFilters} disabled={!hasActiveFilters}>
                Limpar filtros
              </Button>
            </Box>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Busca"
                placeholder="Nome do plano ou recorrencia"
                value={filters.searchParam}
                onChange={handleFilterChange("searchParam")}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                variant="outlined"
                size="small"
                label="Visibilidade"
                value={filters.visibility}
                onChange={handleFilterChange("visibility")}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="public">Publicos</MenuItem>
                <MenuItem value="private">Privados</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={filters.onlyTrial}
                    onChange={handleFilterChange("onlyTrial")}
                  />
                }
                label="So trial"
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper className={classes.tablePaper} elevation={0}>
          <Box className={classes.tableHeader}>
            <Typography variant="body1" className={classes.tableHeaderTitle}>
              Planos operacionais
            </Typography>
            <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              <Chip size="small" label={`${filteredRecords.length} visiveis`} />
              <Chip size="small" label={`${summary.trial || 0} com trial`} />
            </Box>
          </Box>

          {loading && <LinearProgress />}
          <TableContainer className={classes.tableContainer}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Plano</TableCell>
                  <TableCell>Visibilidade</TableCell>
                  <TableCell align="center">Usuarios</TableCell>
                  <TableCell align="center">Conexoes</TableCell>
                  <TableCell align="center">Filas</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Recorrencia</TableCell>
                  <TableCell>Trial</TableCell>
                  <TableCell align="center">Recursos ativos</TableCell>
                  <TableCell>Criado em</TableCell>
                  <TableCell align="right" className={classes.actionsCell}>
                    Acoes
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading &&
                  filteredRecords.map((plan) => (
                    <TableRow
                      key={plan.id}
                      className={
                        plan.trial
                          ? classes.rowTrial
                          : !plan.isPublic
                            ? classes.rowPrivate
                            : ""
                      }
                    >
                      <TableCell>
                        <Typography style={{ fontWeight: 600 }}>{plan.name}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          ID #{plan.id}
                        </Typography>
                      </TableCell>
                      <TableCell>{renderVisibilityChip(plan)}</TableCell>
                      <TableCell align="center">{plan.users ?? 0}</TableCell>
                      <TableCell align="center">{plan.connections ?? 0}</TableCell>
                      <TableCell align="center">{plan.queues ?? 0}</TableCell>
                      <TableCell>{formatCurrency(plan.amount)}</TableCell>
                      <TableCell>{plan.recurrence || "-"}</TableCell>
                      <TableCell>{renderTrialChip(plan)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          icon={<TuneIcon />}
                          label={`${countEnabledFeatures(plan)}/${FEATURE_FIELDS.length}`}
                        />
                      </TableCell>
                      <TableCell className={classes.nowrap}>
                        {formatDateTime(plan.createdAt)}
                      </TableCell>
                      <TableCell align="right" className={classes.actionsCell}>
                        <RowActionsMenu
                          buttonTitle="Acoes do plano"
                          actions={[
                            {
                              key: "edit",
                              label: "Editar plano",
                              icon: EditIcon,
                              onClick: () => handleOpenEditModal(plan),
                            },
                            {
                              key: "delete",
                              label: "Excluir plano",
                              icon: DeleteOutlineIcon,
                              onClick: () => setRecordToDelete(plan),
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && filteredRecords.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="h6">
                {hasActiveFilters
                  ? "Nenhum plano encontrado para os filtros aplicados"
                  : "Nenhum plano cadastrado"}
              </Typography>
            </Box>
          ) : null}
        </Paper>
      </div>
    </div>
  );
};

export default PlansManager;
